import * as React from 'react';
import { translate } from 'react-i18next';
import { IStore } from 'redux/IStore';
const { connect } = require('react-redux');
import * as c from 'models/constants';
import { RouteComponentProps } from 'react-router';
import { getShopCard, resetData, setEditServicesPosition } from 'redux/modules/shop/shopcard';
import { closeModal, showModal } from 'redux/modules/modal';
import { Dispatch } from 'react-redux';
import { IDropdownData, ITranslateProps } from 'models/common';
import { LoadingOverlay } from 'components/Loader';
import { detectIncompatibleBrowsers, getIdFromObject } from 'helpers/common';
import { ServicesListEdit } from './ServicesListEdit';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ServicesSelectMenu } from 'components/FormControls/ServicesSelectMenu';

interface IParams {
	shopId: string;
	officeId?: string;
	serviceCategory?: string;
}

interface IState {
	firstOfficeIdChangeSet: boolean;
	scrollPosition: number;
	hasScrolled: boolean;
	editMode: boolean;
}

interface IProps
	extends RouteComponentProps<IParams, {}>,
		ITranslateProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {}

const mapStateToProps = (state: IStore) => ({
	isFetching: state.shopcard.isFetching,
	shopCard: state.shopcard.shopcard,
	entireShopCard: state.shopcard,
});

const mapDispatchToProps = (dispatch: Dispatch<IStore>, ownProps: RouteComponentProps<IParams, {}>) => ({
	resetData: () => dispatch(resetData()),
	getShopCard: () => dispatch(getShopCard(ownProps.params.shopId, false, true)),
	setEditServicesPosition: (position) => dispatch(setEditServicesPosition(position)),
	showModal: (type: string, modalProps: any) => dispatch(showModal(type, modalProps)),
	closeModal: () => dispatch(closeModal()),
});

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopServicesForm extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.state = {
			firstOfficeIdChangeSet: false,
			scrollPosition: null,
			hasScrolled: false,
			editMode: false,
		};

		this.handleScroll = this.handleScroll.bind(this);
		this.toggleEditMode = this.toggleEditMode.bind(this);
	}

	public componentDidMount() {
		this.props.getShopCard();
		window.addEventListener('scroll', this.handleScroll, { passive: true });
	}

	public componentDidUpdate(prevProps: Readonly<IProps>): void {
		const { params, shopCard, isFetching, entireShopCard } = this.props;
		if (isFetching) {
			return;
		}
		if (params.shopId !== shopCard?.id) {
			this.props.getShopCard();
			return;
		}
		const officeId = params.officeId;
		const nextOfficesIds = (shopCard?.offices || []).map(getIdFromObject);
		if (nextOfficesIds.length && !nextOfficesIds.includes(officeId)) {
			const defaultOfficeId = shopCard.offices.find((off) => off.isDefault)?.id;
			const firstOfficeId = nextOfficesIds[0];
			if (!this.state.firstOfficeIdChangeSet && firstOfficeId) {
				this.setState({ firstOfficeIdChangeSet: true }); // needed after LoginFinished changes otherwise it loops
				return this.changeOfficeIdInURL(defaultOfficeId || firstOfficeId);
			}
			const prevOffices = (prevProps.shopCard.offices || []).map(getIdFromObject).join(';');
			if (prevOffices !== nextOfficesIds.join(';') && firstOfficeId) {
				return this.changeOfficeIdInURL(defaultOfficeId || firstOfficeId);
			}
		}
		// Scroll to the last saved position when coming back from editing/adding a service
		// Lower the subtracting amount if necessary (height changes when loading and adding/removing/editing services so we subtract height from the store saved height)
		if (
			!this.state.hasScrolled &&
			window.document.body.offsetHeight >= entireShopCard.editServicesPagePosition?.entireHeight - 500 &&
			params.officeId === entireShopCard.editServicesPagePosition?.officeId
		) {
			this.setState({ hasScrolled: true });

			if (detectIncompatibleBrowsers()) {
				document.body.scrollTo(0, this.props.entireShopCard.editServicesPagePosition.lastPosition);
			} else {
				window.scrollTo(0, this.props.entireShopCard.editServicesPagePosition.lastPosition);
			}
		}
	}

	public componentWillUnmount() {
		const { params, setEditServicesPosition } = this.props;
		const { scrollPosition } = this.state;
		const entireHeight = window.document.body.offsetHeight;
		const router: any = this.props.router;
		const routerNextLocation: string = router?.location?.pathname;

		if (routerNextLocation.includes('/edit/services/existing') || routerNextLocation.includes('/edit/services/add')) {
			setEditServicesPosition({ entireHeight: entireHeight, lastPosition: scrollPosition, officeId: params.officeId });
		} else {
			setEditServicesPosition(undefined);
		}

		window.removeEventListener('scroll', this.handleScroll);
	}

	public handleScroll = () => {
		const position = Math.round(window.pageYOffset);
		this.setState({ scrollPosition: position });
	};

	private changeOfficeIdInURL(officeId: string, push = false) {
		const { params, router } = this.props;
		const shopId = params.shopId;
		const routerFn = router[push ? 'push' : 'replace'];
		routerFn(`/shop/${shopId}/${officeId}/edit/services`);
	}

	private toggleEditMode = (manualSet?: boolean) => {
		if (manualSet === false) {
			this.setState({ editMode: false });
		} else {
			this.setState({ editMode: !this.state.editMode });
		}
	};

	private get officeSelector(): JSX.Element {
		const { shopCard } = this.props;
		const officesOptions: Array<IDropdownData<string>> = (shopCard?.offices || []).map((l) => ({
			key: l.id,
			value: l.name || l.location,
		}));
		return (
			<ServicesSelectMenu
				items={officesOptions}
				input={{ value: this.props.params.officeId }}
				label={shopCard.name}
				onChangeAction={(officeId) => this.changeOfficeIdInURL(officeId, true)}
				shopPicture={shopCard?.picture}
				searchable={false}
				clearable={false}
				isRequired={true}
			/>
		);
	}

	render() {
		const { editMode } = this.state;
		const { shopCard, params } = this.props;
		if (!params.officeId || !shopCard) {
			return <LoadingOverlay spinner={true} />;
		}

		return (
			<DndProvider backend={HTML5Backend}>
				<div className="editServicesContainer container">
					<div className="shopServices d-flex flex-row justify-content-center">
						<ServicesListEdit
							shopId={params.shopId}
							officeId={params.officeId}
							officeSelector={this.officeSelector}
							route={this.props.route}
							routes={this.props.routes}
							router={this.props.router}
							shopTypes={this.props.shopCard.shopTypes || []}
							serviceCategory={Number(params.serviceCategory) || undefined}
							toggleEditMode={this.toggleEditMode}
							editMode={editMode}
							currencyKey={this.props.shopCard.currencyKey}
						/>
					</div>
				</div>
			</DndProvider>
		);
	}
}

const ShopServices = connect(mapStateToProps, mapDispatchToProps)(ShopServicesForm);

export default ShopServices;
